/** Project browser — the multi-user entry point, not part of the mockups. */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, unpage, type Page } from "../api";
import { canAdminister, ROLE_LABELS, useAuth } from "../auth";
import { Icon, Logo, Wordmark } from "../components/icons";
import {
  Button,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Modal,
  Notice,
  Panel,
  Segmented,
  TextField,
  useToast,
} from "../components/ui";
import { useAsync } from "../hooks";
import { UNIT_SYSTEMS } from "../lib/units";
import type { AuditEvent, Membership, Project, ProjectSummary, Role, UserBrief } from "../types";

export default function Projects() {
  const navigate = useNavigate();
  const { user, logout, setTheme } = useAuth();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  // The working set. Only fields in scope are shown by default — a field set
  // aside is set aside precisely so it stops crowding this page. It must not
  // look deleted, though: the header counts what is hidden and one click on
  // "Set aside" brings it back into view, with its Activate button.
  const [scope, setScope] = useState<"all" | "active" | "inactive">("active");
  const [manageMembers, setManageMembers] = useState<Project | null>(null);

  const projects = useAsync(
    (signal) => api.get<Page<Project>>("/api/projects/", undefined, signal).then((page) => unpage(page, signal)),
    [],
  );
  const setAside = (projects.data ?? []).filter((project) => !project.is_active).length;
  const shown = (projects.data ?? []).filter((project) =>
    scope === "all" ? true : scope === "active" ? project.is_active : !project.is_active,
  );

  return (
    <div style={{ minHeight: "100%", background: "var(--app-bg)" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          height: 46,
          padding: "0 20px",
          background: "var(--titlebar)",
          borderBottom: "1px solid var(--line)",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Logo size={18} />
        <Wordmark size={13} />
        <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-6)" }}>/ Projects</span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          title="Switch theme"
          onClick={() => setTheme(user?.theme === "light" ? "dark" : "light")}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink-4)" }}
        >
          {user?.theme === "light" ? Icon.moon({ size: 14 }) : Icon.sun({ size: 14 })}
        </button>
        <span style={{ font: "400 11px var(--font-sans)", color: "var(--ink-3)" }}>
          {user?.display_name}
        </span>
        <Button variant="ghost" onClick={logout} style={{ height: 27 }}>
          {Icon.logout({ size: 12 })} Keluar
        </Button>
      </header>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 20px 60px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 20, gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <h1 style={{ margin: 0, font: "600 22px var(--font-sans)", color: "var(--ink)" }}>Your projects</h1>
            <p style={{ margin: 0, font: "400 12px var(--font-sans)", color: "var(--ink-5)" }}>
              Every project has its own members and roles.
            </p>
          </div>
          <span style={{ flex: 1 }} />
          <Segmented
            value={scope}
            onChange={setScope}
            options={[
              { value: "active", label: "In scope" },
              { value: "inactive", label: setAside ? `Set aside (${setAside})` : "Set aside" },
              { value: "all", label: "All fields" },
            ]}
          />
          <Button variant="primary" onClick={() => setCreating(true)}>
            {Icon.plus({ size: 13 })} New project
          </Button>
        </div>

        {projects.loading && <Loading />}
        {projects.error && <Notice tone="error">{projects.error}</Notice>}
        {projects.data?.length === 0 && (
          <Empty>
            No project has been shared with you yet.
            <Button variant="primary" onClick={() => setCreating(true)}>
              Create the first project
            </Button>
          </Empty>
        )}
        {!!projects.data?.length && shown.length === 0 && (
          <Empty>
            {scope === "active"
              ? "Every field you can see has been set aside."
              : "No field has been set aside."}
            {scope === "active" && (
              <Button variant="ghost" onClick={() => setScope("inactive")}>
                Show set-aside fields
              </Button>
            )}
          </Empty>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
            gap: 16,
          }}
        >
          {shown.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={() => navigate(`/projects/${project.id}/log`)}
                onMembers={() => setManageMembers(project)}
                onUnits={async (system) => {
                  try {
                    // The setting lives on the project, not the person: a
                    // thickness quoted in a review has to mean the same number
                    // to everyone in the room.
                    await api.patch(`/api/projects/${project.id}/`, { unit_system: system });
                    toast(
                      system === "imperial"
                        ? `"${project.name}" now reads in feet.`
                        : `"${project.name}" now reads in metres.`,
                    );
                    projects.reload();
                  } catch (cause) {
                    toast(cause instanceof Error ? cause.message : String(cause), "error");
                  }
                }}
                onToggleActive={async () => {
                  try {
                    await api.post(`/api/projects/${project.id}/set_active/`, {
                      is_active: !project.is_active,
                    });
                    toast(
                      project.is_active
                        ? `"${project.name}" moved out of the working set.`
                        : `"${project.name}" is back in the working set.`,
                    );
                    projects.reload();
                  } catch (cause) {
                    toast(cause instanceof Error ? cause.message : String(cause), "error");
                  }
                }}
              />
            ))}
        </div>
      </div>

      {creating && (
        <CreateProjectDialog
          onClose={() => setCreating(false)}
          onCreated={(project) => {
            toast(`Project "${project.name}" created.`);
            setCreating(false);
            navigate(`/projects/${project.id}/log`);
          }}
        />
      )}
      {manageMembers && (
        <MembersDialog
          project={manageMembers}
          onClose={() => {
            setManageMembers(null);
            projects.reload();
          }}
        />
      )}
    </div>
  );
}

function ProjectCard({
  project,
  onOpen,
  onMembers,
  onToggleActive,
  onUnits,
}: {
  project: Project;
  onOpen: () => void;
  onMembers: () => void;
  onToggleActive: () => void;
  onUnits: (system: string) => void;
}) {
  const summary = useAsync(
    (signal) => api.get<ProjectSummary>(`/api/projects/${project.id}/summary/`, undefined, signal),
    [project.id],
  );
  const activity = useAsync(
    (signal) => api.get<AuditEvent[]>(`/api/projects/${project.id}/activity/`, undefined, signal),
    [project.id],
  );

  const mayToggle = canAdminister(project.my_role);

  return (
    <Panel
      title={project.code}
      meta={
        project.is_active
          ? (project.my_role ? ROLE_LABELS[project.my_role] : undefined)
          : "set aside"
      }
      bodyStyle={{ padding: 13, gap: 11, opacity: project.is_active ? 1 : 0.62 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <button
          type="button"
          onClick={onOpen}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            textAlign: "left",
            cursor: "pointer",
            font: "600 14px var(--font-sans)",
            color: "var(--ink)",
          }}
        >
          {project.name}
        </button>
        <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-6)" }}>
          {[project.basin, project.field].filter(Boolean).join(" · ") || "—"}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <KeyValue
          k="Wells"
          v={summary.data ? `${summary.data.wells_evaluated} / ${summary.data.wells_total} evaluated` : "—"}
        />
        <KeyValue k="Curves" v={project.curve_count} />
        <KeyValue k="Net pay total" v={summary.data ? `${summary.data.net_pay_total} m` : "—"} />
        <KeyValue k="Anggota" v={project.member_count} />
      </div>

      {summary.data && summary.data.rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="label">Highest net pay</span>
          {summary.data.rows.slice(0, 3).map((row) => (
            <div key={row.well_id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-3)", width: 84 }}>
                {row.well}
              </span>
              <div style={{ flex: 1, height: 5, background: "var(--line)", borderRadius: 3, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.min(100, ((row.net_pay ?? 0) / Math.max(summary.data!.rows[0].net_pay ?? 1, 1)) * 100)}%`,
                    height: "100%",
                    background: "var(--accent)",
                  }}
                />
              </div>
              <span style={{ font: "500 10px var(--font-mono)", color: "var(--ink-2)", width: 48, textAlign: "right" }}>
                {row.net_pay?.toFixed(1) ?? "—"} m
              </span>
              {row.approved && <span title="Disetujui">{Icon.check({ size: 10, color: "var(--green)" })}</span>}
            </div>
          ))}
        </div>
      )}

      {activity.data && activity.data.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span className="label">Latest activity</span>
          {activity.data.slice(0, 2).map((event) => (
            <span key={event.id} style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
              {event.actor_detail?.display_name ?? "Sistem"} {event.verb_label} {event.target_label}
            </span>
          ))}
        </div>
      )}

      {!project.is_active && (
        <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
          Out of scope for analysis. Every well, curve and run is still here and
          still readable — only the working set has changed.
        </span>
      )}

      {mayToggle && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="label">Unit system</span>
          <Segmented
            value={project.unit_system === "imperial" ? "imperial" : "metric"}
            onChange={(value) => onUnits(value)}
            options={UNIT_SYSTEMS.map((u) => ({ value: u.value, label: u.label }))}
          />
          <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
            {UNIT_SYSTEMS.find((u) => u.value === (project.unit_system === "imperial" ? "imperial" : "metric"))?.note}
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
        <Button variant="primary" onClick={onOpen} style={{ flex: 1 }}>
          Open workstation
        </Button>
        {mayToggle && (
          <Button
            variant="ghost"
            onClick={onToggleActive}
            title={project.is_active ? "Move out of the working set" : "Return to the working set"}
          >
            {project.is_active ? "Set aside" : "Activate"}
          </Button>
        )}
        {mayToggle && (
          <Button variant="ghost" onClick={onMembers} title="Manage members">
            {Icon.user({ size: 13 })}
          </Button>
        )}
      </div>
    </Panel>
  );
}

function CreateProjectDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (project: Project) => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [basin, setBasin] = useState("");
  const [field, setField] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const slug = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const project = await api.post<Project>("/api/projects/", {
        name,
        code: code || slug(name),
        basin,
        field,
      });
      onCreated(project);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!name.trim()} onClick={submit}>
            Create project
          </Button>
        </>
      }
    >
      <TextField label="Project name" value={name} onChange={setName} autoFocus />
      <TextField
        label="Kode (unik)"
        value={code}
        onChange={setCode}
        placeholder={name ? slug(name) : "mis. kti-3d"}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TextField label="Cekungan" value={basin} onChange={setBasin} />
        <TextField label="Fields" value={field} onChange={setField} />
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      <Notice tone="info">You become the owner automatically and can invite other members.</Notice>
    </Modal>
  );
}

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "owner", label: "Owner — manage members, approve results" },
  { value: "petrophysicist", label: "Petrophysicist — compute & publish" },
  { value: "interpreter", label: "Interpreter — edit data, no computing" },
  { value: "viewer", label: "Viewer — read only" },
];

function MembersDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [error, setError] = useState<string | null>(null);

  const members = useAsync(
    (signal) => api.get<Membership[]>(`/api/projects/${project.id}/members/`, undefined, signal),
    [project.id],
  );
  const directory = useAsync(
    (signal) => api.get<UserBrief[]>("/api/users/", { q: query }, signal).then((page) => unpage(page, signal)),
    [query],
  );

  const add = async (userId: number) => {
    setError(null);
    try {
      await api.post(`/api/projects/${project.id}/members/add/`, { user: userId, role });
      toast("Member added.");
      members.reload();
      setQuery("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const changeRole = async (membershipId: number, nextRole: Role) => {
    setError(null);
    try {
      await api.patch(`/api/projects/${project.id}/members/${membershipId}/`, { role: nextRole });
      members.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const remove = async (membershipId: number) => {
    setError(null);
    try {
      await api.delete(`/api/projects/${project.id}/members/${membershipId}/`);
      members.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const existing = new Set(members.data?.map((m) => m.user) ?? []);

  return (
    <Modal
      title={`Members — ${project.name}`}
      onClose={onClose}
      width={540}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {error && <Notice tone="error">{error}</Notice>}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="label">Current members</span>
        {members.loading && <Loading />}
        {members.data?.map((membership) => (
          <div
            key={membership.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "7px 9px",
              background: "var(--panel-inset)",
              border: "1px solid var(--line-strong)",
              borderRadius: 6,
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "var(--accent-wash)",
                border: "1px solid var(--accent)",
                font: "600 9px var(--font-mono)",
                color: "var(--accent-pale)",
                flex: "none",
              }}
            >
              {membership.user_detail.initials}
            </span>
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span style={{ font: "500 12px var(--font-sans)", color: "var(--ink)" }}>
                {membership.user_detail.display_name}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                {membership.user_detail.discipline || membership.user_detail.username}
              </span>
            </div>
            <Dropdown
              value={membership.role}
              options={ROLE_OPTIONS.map((option) => ({
                value: option.value,
                label: ROLE_LABELS[option.value],
              }))}
              onChange={(next) => changeRole(membership.id, next)}
              width={140}
              compact
            />
            <Button variant="danger" onClick={() => remove(membership.id)} title="Cabut akses" style={{ height: 26, padding: "0 9px" }}>
              {Icon.trash({ size: 12 })}
            </Button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="label">Add member</span>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <TextField label="Search username" value={query} onChange={setQuery} placeholder="e.g. ratna" />
          </div>
          <Dropdown
            value={role}
            options={ROLE_OPTIONS.map((option) => ({ value: option.value, label: ROLE_LABELS[option.value] }))}
            onChange={setRole}
            width={150}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 180, overflowY: "auto" }}>
          {directory.data
            ?.filter((candidate) => !existing.has(candidate.id))
            .map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                onClick={() => add(candidate.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  padding: "6px 9px",
                  border: "1px solid var(--line-strong)",
                  borderRadius: 6,
                  background: "transparent",
                  color: "var(--ink-2)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {Icon.plus({ size: 12 })}
                <span style={{ font: "500 11.5px var(--font-sans)" }}>{candidate.display_name}</span>
                <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                  {candidate.discipline}
                </span>
              </button>
            ))}
        </div>
      </div>

      <Notice tone="info">
        A role governs actions on this project only: a viewer reads, an interpreter edits data,
        a petrophysicist runs computations, an owner manages members and approves results.
      </Notice>
    </Modal>
  );
}
